import type { StaticEntityType, StoryType } from "@config";
import safeCompile, { escapeJsonControlChars } from "@lib/handlebars";
import {
  parseChartListDynamicVar,
  type ChartProps,
  type IChartListDynamicVarData,
} from "@library-elements/chart";
import {
  parseTableDataDynamicVar,
  parseTableListDynamicVar,
  type ITableListDynamicVarData,
  type ITableSettings,
} from "@library-elements/table";
import { pruneOptionalSlides } from "@features/dynamic-variables/make-optional/resolve";
import { produce } from "immer";
import {
  parseListDynamicVariable,
  parseListDynamicVariableForSlides,
} from "./parse-list-dynamic-variable";

const CUSTOM_MAP_ORG_ID = [
  "5eca9eb2a25703fefbdfc3f83270509e",
  "b7548deac51c2279ebfe1f8548369419",
  "ea6f9d3d2603f9f37cc762e16d1f7495",
  "426d0d9441f374fd117888c0394d4fba",
  "e360475ed0c765f03916e87dbeb362f1",
];

export const compileStory = (
  story: StoryType,
  data: Record<string, unknown>,
  skipHandlebars?: boolean,
) => {
  try {
    const compiled = skipHandlebars
      ? story
      : JSON.parse(
          safeCompile(
            JSON.stringify(story),
            escapeJsonControlChars({ ...data, system: data }) as Record<
              string,
              unknown
            >,
          ),
        );
    const arrayKeys = new Set<string>();

    Object.entries(data).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        arrayKeys.add(key);
      }
    });

    return produce(compiled, (draftCompiled: Record<string, any>) => {
      let filteredSlides = draftCompiled.slides.filter(
        (slide: StaticEntityType) =>
          !slide.settings.isDynamicallyHidden ||
          !executeEval(data, slide.settings.isDynamicallyHidden),
      );

      // Drop entities the sender excluded (simple mode) or whose condition
      // resolves to hidden (conditional mode), at every layer. Runs against the
      // version `data`, so the viewer never receives the excluded content.
      filteredSlides = pruneOptionalSlides(filteredSlides, data);

      if (CUSTOM_MAP_ORG_ID.includes(story.orgId)) {
        filteredSlides = filteredSlides
          .flatMap((slide: StaticEntityType) => {
            if (
              slide.settings.listDynamicVariable &&
              data?.[slide.settings.listDynamicVariable]
            ) {
              return parseListDynamicVariableForSlides(
                slide,
                data[slide.settings.listDynamicVariable] as Record<
                  string,
                  string
                >[],
                slide.settings.listDynamicVariable as string,
              );
            }
            return [slide];
          })
          .reduce((acc: StaticEntityType[], slide: StaticEntityType) => {
            if (Array.isArray(slide.children)) {
              const filteredChildren = slide.children.filter(
                (combo: any) =>
                  combo?.settings?.hideComboEntityCustomVariable !== "true",
              );
              if (filteredChildren.length > 0) {
                acc.push({ ...slide, children: filteredChildren });
              }
            }
            return acc;
          }, [] as StaticEntityType[]);
      }

      filteredSlides.forEach((slide: StaticEntityType) => {
        if (slide.settings.listDynamicVariable) {
          delete slide.settings.listDynamicVariable;
        }
        if (slide.children) {
          slide.children.forEach((combo) => {
            const { listDynamicVariable, listDynamicVariableData, ...rest } =
              combo.settings;

            // `listDynamicVariableData` is present when this slide was expanded
            // per item of a slide-level list variable — it carries the item's
            // nested array so we resolve from the item scope, not the root data.
            const comboListData = (listDynamicVariableData ??
              data[listDynamicVariable]) as Record<string, string>[];

            if (
              listDynamicVariable &&
              (listDynamicVariableData || arrayKeys.has(listDynamicVariable))
            ) {
              combo.settings = rest;
              combo.children = parseListDynamicVariable(
                combo.children,
                comboListData,
                listDynamicVariable as string,
              );
            }

            // Always walk the combo, expanded or not. When it was expanded,
            // `parseListDynamicVariable` has injected each item's nested list
            // value onto the cloned elements, and only this walk resolves them
            // — skipping it leaves nested charts/tables inside a repeated item
            // unresolved (static-library resolves them at render time).
            compileComboElements(combo, data);
          });
        }
      });

      draftCompiled.slides = filteredSlides;
    });
  } catch (error) {
    console.error(error);
    return story;
  }
};

// Walk a combo's set -> group -> element tree and resolve each element's
// variables. Shared by expanded (list-variable) and plain combos.
function compileComboElements(
  combo: StaticEntityType,
  data: Record<string, unknown>,
) {
  combo.children?.forEach((set) => {
    set.children?.forEach((group) => {
      group.children?.forEach((element) => {
        compileElement(element, data);
        // Tables 2.0 in-cell elements: a table element may own
        // element children — resolve their variables too.
        element.children?.forEach((child) => compileElement(child, data));
      });
    });
  });
}

// Per-element variable resolution (list vars for Table/Chart, custom image
// src). Shared by slide-level elements and in-cell element children.
function compileElement(
  element: StaticEntityType,
  data: Record<string, unknown>,
) {
  const {
    listDynamicVariable: elListDynamicVar,
    listDynamicVariableData: elListDynamicVarData,
    customDynamicVariable: elCustomDynamicVariable,
    dataDynamicVariable: elDataDynamicVar,
    ...elRest
  } = element.settings;

  // Prefer item-scoped data (injected when the slide was expanded per item of
  // a slide-level list variable) so nested charts/tables resolve their own
  // list variable.
  const elListData = elListDynamicVarData ?? data[elListDynamicVar as string];

  // A `data` variable owns the WHOLE table: its 2D matrix replaces the cells,
  // rows and columns (vs. listDynamicVariable, which clones one template row per
  // record). The binding is stored as `{{token}}` so the fs-vars manifest scan
  // still finds it, so strip the braces before the lookup.
  if (element.settings.name === "Table" && typeof elDataDynamicVar === "string") {
    const dataVarName = elDataDynamicVar
      .replace(/^\{\{+/, "")
      .replace(/\}\}+$/, "")
      .trim();
    const matrix = dataVarName ? data[dataVarName] : undefined;
    element.settings = {
      ...parseTableDataDynamicVar({
        settings: elRest as unknown as ITableSettings,
        matrix,
      }),
    };
  }

  if (element.settings.name === "Table" && elListData) {
    element.settings = {
      ...parseTableListDynamicVar({
        settings: elRest as unknown as ITableSettings,
        data: elListData as ITableListDynamicVarData,
      }),
    };
  }
  if (element.settings.name === "Chart" && elListData) {
    element.settings = {
      ...parseChartListDynamicVar({
        settings: elRest as unknown as ChartProps,
        data: elListData as IChartListDynamicVarData,
      }),
    };
  }
  if (
    element.settings.name === "Image" &&
    typeof elCustomDynamicVariable === "string" &&
    elCustomDynamicVariable
  ) {
    if (elCustomDynamicVariable.startsWith("https://")) {
      element.settings.src = elCustomDynamicVariable;
    }
    delete element.settings.customDynamicVariable;
  }
}

function executeEval(data: Record<string, unknown>, code: string) {
  try {
    // eslint-disable-next-line no-new-func
    const func = new Function("data", `return ${code}`);
    return func(data);
  } catch (err) {
    console.error(`failed to run ${code}:`, err);
    return "";
  }
}
